import React from "react";
import { BrowserRouter, Routes as RouterRoutes, Route, Navigate } from "react-router-dom";
import ScrollToTop from "components/ScrollToTop";
import ErrorBoundary from "components/ErrorBoundary";
import ProtectedRoute from "components/ProtectedRoute";
import NotFound from "pages/NotFound";
import LoginPage from './pages/admin';
import CandidateDatabase from './pages/candidate-database';
import CVUploadProcessing from './pages/cv-upload-processing';
import CandidateProfileDetail from './pages/candidate-profile-detail';
import HRDashboard from './pages/hr-dashboard';
import InterviewScheduling from './pages/interview-scheduling';
import HRManagerDashboard from './pages/hr-roles/manager';
import HRRecruiterDashboard from './pages/hr-roles/recruiter';
import HRCoordinatorDashboard from './pages/hr-roles/coordinator';
import TaskManagementPage from './pages/hr-roles/coordinator/TaskManagementPage';
import TeamManagementPage from './pages/hr-roles/manager/team-management';
import UserManagementPage from './pages/user-management';
import JobManagementPage from './pages/job-management';
import CreateJobPage from './pages/job-management/CreateJobPage';
import ConnectionTest from './components/ConnectionTest';
import AuthTest from './components/AuthTest';
import ManagerFlowTest from './components/ManagerFlowTest';
import LogoutTest from './components/LogoutTest';

// Public job portal pages
import PublicJobPortal from './pages/public/job-portal';
import JobApplicationForm from './pages/public/job-application/JobApplicationForm';
import ApplicationStatusTracker from './pages/public/application-tracker';
import CandidateRegistration from './pages/public/candidate-registration/CandidateRegistration';
import RegistrationSuccess from './pages/public/registration-success/RegistrationSuccess';
import PublicLogin from './pages/public/login/PublicLogin';
import PublicDashboard from './pages/public/candidate-dashboard/CandidateDashboard';
import ApplicationTracker from './pages/candidate/ApplicationTracker';
import AboutPage from './pages/public/about/AboutPage';
import ContactPage from './pages/public/contact/ContactPage';

const Routes = () => {
  return (
    <BrowserRouter>
      <ErrorBoundary>
      <ScrollToTop />
      <RouterRoutes>
        {/* Public Routes - Job Portal */}
        <Route path="/public/jobs" element={<PublicJobPortal />} />
        <Route path="/public/jobs/:jobId/apply" element={<JobApplicationForm />} />
        <Route path="/public/track-application" element={<ApplicationStatusTracker />} />
        <Route path="/public/register" element={<CandidateRegistration />} />
        <Route path="/public/registration-success" element={<RegistrationSuccess />} />
        <Route path="/public/login" element={<PublicLogin />} />
        <Route path="/public/about" element={<AboutPage />} />
        <Route path="/public/contact" element={<ContactPage />} />
        <Route path="/public/candidate-dashboard" element={
          <ProtectedRoute requiredRoles={['candidate']}>
            <PublicDashboard />
          </ProtectedRoute>
        } />
        <Route path="/candidate/applications" element={
          <ProtectedRoute requiredRoles={['candidate']}>
            <ApplicationTracker />
          </ProtectedRoute>
        } />
        
        {/* Authentication */}
        <Route path="/login" element={<LoginPage />} />
        
        {/* Development/Testing Routes (accessible without auth for debugging) */}
        <Route path="/test-connection" element={<ConnectionTest />} />
        <Route path="/test-auth" element={<AuthTest />} />
        <Route path="/test-manager-flow" element={<ManagerFlowTest />} />
        <Route path="/test-logout" element={<LogoutTest />} />
        
        {/* Protected Routes - All main app functionality requires authentication */}
        <Route path="/hr-dashboard" element={
          <ProtectedRoute>
            <HRDashboard />
          </ProtectedRoute>
        } />
        
        <Route path="/candidate-database" element={
          <ProtectedRoute>
            <CandidateDatabase />
          </ProtectedRoute>
        } />
        
        <Route path="/cv-upload-processing" element={
          <ProtectedRoute>
            <CVUploadProcessing />
          </ProtectedRoute>
        } />
        
        <Route path="/candidate-profile-detail" element={
          <ProtectedRoute>
            <CandidateProfileDetail />
          </ProtectedRoute>
        } />
        
        <Route path="/interview-scheduling" element={
          <ProtectedRoute>
            <InterviewScheduling />
          </ProtectedRoute>
        } />
        
        {/* HR Role Dashboards with Role-Based Access Control */}
        <Route path="/hr-roles/manager" element={
          <ProtectedRoute requiredRoles={['admin', 'hr_manager']}>
            <HRManagerDashboard />
          </ProtectedRoute>
        } />
        
        <Route path="/hr-roles/recruiter" element={
          <ProtectedRoute requiredRoles={['admin', 'hr_manager', 'recruiter']}>
            <HRRecruiterDashboard />
          </ProtectedRoute>
        } />
        
        <Route path="/hr-roles/coordinator" element={
          <ProtectedRoute requiredRoles={['admin', 'hr_manager', 'hr_coordinator', 'hr_user']}>
            <HRCoordinatorDashboard />
          </ProtectedRoute>
        } />
        
        <Route path="/hr-roles/coordinator/tasks" element={
          <ProtectedRoute requiredRoles={['admin', 'hr_manager', 'hr_coordinator', 'recruiter']}>
            <TaskManagementPage />
          </ProtectedRoute>
        } />
        
        <Route path="/team-management" element={
          <ProtectedRoute requiredRoles={['admin', 'hr_manager']}>
            <TeamManagementPage />
          </ProtectedRoute>
        } />
        
        <Route path="/user-management" element={
          <ProtectedRoute requiredRoles={['admin', 'hr_manager']}>
            <UserManagementPage />
          </ProtectedRoute>
        } />
        
        {/* Job Management Routes */}
        <Route path="/job-management" element={
          <ProtectedRoute requiredRoles={['admin', 'hr_manager', 'recruiter']}>
            <JobManagementPage />
          </ProtectedRoute>
        } />
        
        <Route path="/job-management/create" element={
          <ProtectedRoute requiredRoles={['admin', 'hr_manager', 'recruiter']}>
            <CreateJobPage />
          </ProtectedRoute>
        } />
        
        {/* Default route - redirect to public job portal for general users, HR dashboard for authenticated users */}
        <Route path="/" element={<Navigate to="/public/jobs" replace />} />
        <Route path="/admin" element={
          <ProtectedRoute>
            <HRDashboard />
          </ProtectedRoute>
        } />
        
        {/* 404 fallback */}
        <Route path="*" element={<NotFound />} />
      </RouterRoutes>
      </ErrorBoundary>
    </BrowserRouter>
  );
};

export default Routes;
